/*
 *  -----------------------------------------------------------------------
 *    <copyright file="add-action.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import 'date-fns';
import { pick } from 'lodash';
import moment from 'moment';
import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client/react';

import { useTranslation, useBoolean } from 'hooks';
import { SAVE_CM_ACTION, GET_CM_OBSERVATIONS_AND_ACTIONS } from 'queries/central-monitoring';

import Constants from 'utils/constants';
import { formatDateUTC_DEPRECATED } from 'utils/convertor';
import { validateFields, validateDate } from 'utils/validation';
import { ActionUpdateInterface } from 'views/components/central-monitoring/action-update.interface';
import { SaveButton } from 'views/components/core/buttons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import DatePicker from 'views/components/core/date-picker';
import { CustomDialog, DialogActions, DialogTitle } from 'views/components/core/dialog';
import ModalUpdatedInfo from 'views/components/core/modal-updated-info';
import SelectWithCategories from 'views/components/core/select-with-categories';
import { SelectWithSearchDataArrayInterface } from 'views/components/core/select-with-categories/select-with-categories.interface';
import TextField from 'views/components/core/text-field';

const AddAction = ({
    study,
    open,
    currentAction,
    onClose,
    observationId,
    riskId,
    siteInfo,
    dataset,
    canEdit,
    owners,
}: any) => {
    const { t } = useTranslation();
    const [saveVariable] = useMutation(SAVE_CM_ACTION);

    const initialState = {
        id: null as any,
        description: '',
        ownerId: Constants.ObjectId.Empty,
        result: '',
        actionTaken: '',
        rootCause: '',
        expectedCompleteDate: null as any,
        requestDate: null as any,
        completedDate: null as any,
    };

    useEffect(() => {
        if (owners) {
            const array = [...owners];
            array.unshift({
                id: Constants.ObjectId.Empty,
                text: t('selectOwner'),
                default: true,
            });
            setOwnerOptions(array);
        }
    }, [owners]);

    const disabledSave = useBoolean(true);

    const [state, setState] = useState({ ...initialState });
    const [errors, setErrors] = useState({} as any);
    const [ownerOptions, setOwnerOptions] = useState([] as SelectWithSearchDataArrayInterface[]);

    useEffect(() => {
        if (open) {
            if (currentAction)
                setState({
                    id: currentAction.id || null,
                    description: currentAction.description || '',
                    ownerId: currentAction.ownerId || Constants.ObjectId.Empty,
                    result: currentAction.result || '',
                    rootCause: currentAction.rootCause || '',
                    actionTaken: currentAction.actionTaken || '',
                    expectedCompleteDate:
                        moment(currentAction.expectedCompleteDate).year() > 1
                            ? formatDateUTC_DEPRECATED(currentAction.expectedCompleteDate, true)
                            : null,
                    requestDate:
                        moment(currentAction.requestDate).year() > 1
                            ? formatDateUTC_DEPRECATED(currentAction.requestDate, true)
                            : null,
                    completedDate:
                        moment(currentAction.completedDate).year() > 1
                            ? formatDateUTC_DEPRECATED(currentAction.completedDate, true)
                            : null,
                });
            else setState({ ...initialState });
        }
    }, [currentAction, open]);

    const close = (action?: string) => {
        disabledSave.setTrue();
        if (action !== 'error') {
            setState({ ...initialState });
            setErrors({});
        }
        onClose(action);
    };

    const onChange = (name: string) => (event: any) => {
        disabledSave.setFalse();
        const length = 1200;

        setState({
            ...state,
            [name]: event.target.value,
        });
        validate(event.target.value, name, length, false);
    };

    const validate = (value?: string, field?: string, length?: number, required?: boolean) => {
        const error = validateFields(value, field, length, required);
        setErrors({
            ...errors,
            ...error,
        });
    };

    const onDateChange = (name: string) => (event: any) => {
        disabledSave.setFalse();
        if (name === 'requestDate')
            setErrors({
                ...errors,
                requestDate: null,
            });
        setState({
            ...state,
            [name]: event?.value ? moment(event?.value) : null,
        });
    };

    const saveAction = () => {
        const newAction = pick(state, ActionUpdateInterface);
        disabledSave.setTrue();

        saveVariable({
            variables: {
                observationId: observationId || currentAction.observationId || null,
                action: {
                    ...newAction,
                    requestDate: formatDateUTC_DEPRECATED(state.requestDate),
                    expectedCompleteDate: formatDateUTC_DEPRECATED(state.expectedCompleteDate),
                    completedDate: formatDateUTC_DEPRECATED(state.completedDate),
                },
            },
            refetchQueries: [
                {
                    query: GET_CM_OBSERVATIONS_AND_ACTIONS,
                    variables: {
                        studyId: study.id,
                    },
                },
            ],
        })
            .then(() => {
                disabledSave.setFalse();
                close('edit');
            })
            .catch(() => {
                disabledSave.setFalse();
                close('error');
            });
    };

    const createAndAssignAction = () => {
        disabledSave.setTrue();
        saveVariable({
            variables: {
                riskId: riskId || null,
                observationId: observationId || null,
                action: {
                    ...state,
                    requestDate: formatDateUTC_DEPRECATED(state.requestDate),
                    expectedCompleteDate: formatDateUTC_DEPRECATED(state.expectedCompleteDate),
                    completedDate: formatDateUTC_DEPRECATED(state.completedDate),
                },
            },
            refetchQueries: [
                {
                    query: GET_CM_OBSERVATIONS_AND_ACTIONS,
                    variables: {
                        studyId: study.id,
                        siteId: siteInfo?.id,
                        datasetId: dataset?.id,
                    },
                },
            ],
        })
            .then(() => {
                disabledSave.setFalse();
                close('assign');
            })
            .catch(() => {
                disabledSave.setFalse();
                close('error');
            });
    };

    const validateDates = () =>
        validateDate(state.requestDate) &&
        validateDate(state.expectedCompleteDate) &&
        validateDate(state.completedDate);

    return (
        <CustomDialog onClose={close} aria-labelledby="add-action" fullWidth maxWidth="md" scroll="paper" open={open}>
            <DialogTitle id="add-action-modal-title">
                {currentAction ? `${t('ra:editAction')}: ${currentAction.recordId}` : t('ra:addAction')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => {
                        close();
                    }}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent style={{ paddingLeft: 15, paddingRight: 15, paddingTop: 20 }}>
                <div className="selects-wrapper flex-end">
                    <FormControl variant="outlined" className="form-action-type mr">
                        <DatePicker
                            label={`${t('ra:requestedDate')}*`}
                            customId="edit-action-requested-date"
                            labelWidth={135}
                            selectedDate={state.requestDate}
                            handleDateChange={onDateChange('requestDate')}
                            error={errors.requestDate}
                            setValid={(value: boolean) => {
                                if (value === false)
                                    setErrors({
                                        ...errors,
                                        requestDate: t('valueInvalid'),
                                    });
                            }}
                        />
                        {errors.requestDate && <p className="request-date-error">{errors.requestDate}</p>}
                    </FormControl>
                    <SelectWithCategories
                        label={t('owner')}
                        sortDescriptors={[{ field: 'text', dir: 'asc' }]}
                        dataArray={ownerOptions}
                        onChange={onChange}
                        fieldSelect="ownerId"
                        currentState={state.ownerId}
                        searchable
                        labelField="owner"
                        appendPopupToSelect
                        showWarning={
                            currentAction &&
                            currentAction._owner &&
                            currentAction._owner.id === state.ownerId &&
                            currentAction._owner.active === false
                        }
                        customClassName={`${
                            currentAction &&
                            currentAction._owner &&
                            currentAction._owner.id === state.ownerId &&
                            currentAction._owner.active === false
                                ? 'form-action-type inactive'
                                : 'form-action-type'
                        }`}
                    />
                </div>
                <TextField
                    label={`${t('description')}*`}
                    id="edit-action-description"
                    multiline
                    rows={5}
                    fullWidth
                    value={state.description}
                    onChange={onChange('description')}
                    size="small"
                    placeholder={t('enterValue')}
                    helperText={
                        <CharactersRemaining
                            id="add-action-statement-description"
                            length={state.description.length}
                            max={1200}
                            error={errors.description}
                        />
                    }
                    variant="outlined"
                    color="secondary"
                    className="mb"
                    error={!!errors.description}
                />
                <TextField
                    label={t('result')}
                    id="edit-action-result"
                    multiline
                    rows={5}
                    fullWidth
                    value={state.result}
                    onChange={onChange('result')}
                    size="small"
                    placeholder={t('enterValue')}
                    helperText={
                        <CharactersRemaining
                            id="add-action-statement-result"
                            length={state.result.length}
                            max={1200}
                            error={errors.result}
                        />
                    }
                    variant="outlined"
                    color="secondary"
                    className="mb"
                    error={!!errors.result}
                />
                <TextField
                    label={t('cm:actionTaken')}
                    id="edit-action-actionTaken"
                    multiline
                    rows={5}
                    fullWidth
                    value={state.actionTaken}
                    onChange={onChange('actionTaken')}
                    size="small"
                    placeholder={t('enterValue')}
                    helperText={
                        <CharactersRemaining
                            id="add-action-actionTaken"
                            length={state.actionTaken.length}
                            max={1200}
                            error={errors.actionTaken}
                        />
                    }
                    variant="outlined"
                    color="secondary"
                    className="mb"
                    error={!!errors.actionTaken}
                />
                <TextField
                    label={t('cm:rootCause')}
                    id="edit-action-rootCause"
                    multiline
                    rows={5}
                    fullWidth
                    value={state.rootCause}
                    onChange={onChange('rootCause')}
                    size="small"
                    placeholder={t('enterValue')}
                    helperText={
                        <CharactersRemaining
                            id="add-action-rootCause"
                            length={state.rootCause.length}
                            max={1200}
                            error={errors.rootCause}
                        />
                    }
                    variant="outlined"
                    color="secondary"
                    className="mb"
                    error={!!errors.rootCause}
                />
                <div className="selects-wrapper">
                    <FormControl variant="outlined" className="form-action-type mr">
                        <DatePicker
                            label={t('ra:expectedCompletionDate')}
                            customId="edit-action-completion-date"
                            labelWidth={220}
                            error={errors.expectedCompleteDate}
                            selectedDate={state.expectedCompleteDate}
                            placeholder={t('ra:enterExpectedCompletionDate')}
                            handleDateChange={onDateChange('expectedCompleteDate')}
                            setValid={(value: boolean) => {
                                if (value === false)
                                    setErrors({
                                        ...errors,
                                        expectedCompleteDate: t('valueInvalid'),
                                    });
                            }}
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-action-type">
                        <DatePicker
                            label={t('subject:completedDate')}
                            customId="edit-action-completed-date"
                            labelWidth={135}
                            error={errors.completedDate}
                            placeholder={t('ra:enterCompletedDate')}
                            selectedDate={state.completedDate}
                            disabled={currentAction?._isCompletedDateReadOnly}
                            handleDateChange={onDateChange('completedDate')}
                            setValid={(value: boolean) => {
                                if (value === false)
                                    setErrors({
                                        ...errors,
                                        completedDate: t('valueInvalid'),
                                    });
                            }}
                        />
                    </FormControl>
                </div>
                <DialogActions className={`actions-info-wrapper-edit-cv ${!currentAction ? 'flex-end' : ''}`}>
                    <ModalUpdatedInfo item={currentAction} />
                    <SaveButton
                        className="edit-cv-update-btn"
                        id="edit-action-save"
                        text={currentAction ? 'common:update' : 'ra:createAssign'}
                        disabled={
                            Object.keys(errors).filter((item) => errors[item]).length > 0 ||
                            disabledSave.value ||
                            !canEdit
                        }
                        onClick={() => {
                            const error = validateFields(state.description, 'description', 1200, true);
                            setErrors({
                                ...errors,
                                ...error,
                                requestDate: state.requestDate === null ? t('ra:requiredFieldError') : null,
                            });
                            if ((error && error.description) || state.requestDate === null || !validateDates()) return;

                            if (currentAction) {
                                saveAction();
                            } else {
                                createAndAssignAction();
                            }
                        }}
                    />
                </DialogActions>
            </DialogContent>
        </CustomDialog>
    );
};

export default AddAction;
